/**
 * ONE LINE OF AN ATTENDANCE NOTE, SAVED WITHOUT LOSING ANYBODY ELSE'S LINE.
 *
 * attendanceNotes/{offeringId_date_studentId}.notes is ONE string that more
 * than one writer adds a line to: the teacher's absent note (lib/
 * attendanceAbsentNote.ts) and RosterPanel's "Alternate covered: <name>"
 * (lib/alternates.ts, withStepInNote). Firestore's own { merge: true } works on
 * FIELDS, not on lines inside one string, so it cannot protect either line.
 *
 * WHAT WENT WRONG FIRST (the #1293 refute round, proven in the rules emulator,
 * tests/attendance-note-race-rules.test.mjs): each writer merged its line onto
 * the copy of the note ITS SCREEN held, then wrote the whole string back. Two
 * staff phones saving within a second of each other each wrote over the
 * other's line (R1 and R2), and a save queued offline landed later over a
 * cover recorded in the meantime (R3).
 *
 * THE FIX: a TRANSACTION. Read the note as the server holds it right now,
 * apply this writer's merge to THAT text, and write it back; if somebody else
 * wrote in between, Firestore re-runs the merge on their version. Zero rules
 * bytes: a transaction is the same get, update and set the rules already
 * allow.
 *
 * THE PRICE, SAID PLAINLY: a transaction needs signal. There is no "saved on
 * this phone" for a note any more, because a note queued offline is exactly
 * the write that landed over somebody's cover line in R3. Without signal the
 * save is refused with words that say so, and the teacher tries again.
 *
 * The mark is touched in the same transaction (recordedBy and updatedAt only)
 * for the same reason useSaveAttendanceNote always did it: it needs the mark
 * to exist, and it lets the rule's correction window refuse a stale night for
 * the note exactly as it does for the chip.
 *
 * Takes the Firestore instance as an argument and imports nothing from the
 * app, so the rules emulator test drives THIS function, not a copy of it.
 */
import { doc, runTransaction, serverTimestamp, type Firestore } from 'firebase/firestore';

/** The rule's own ceiling on the note text. */
export const ATTENDANCE_NOTE_TEXT_MAX = 2000;

/** How long a note save waits before it says it needs signal. */
export const ATTENDANCE_NOTE_SAVE_TIMEOUT_MS = 10_000;

/** The merged note would not fit: refused rather than cut. */
export class AttendanceNoteFullError extends Error {
  readonly code = 'note-full' as const;
  constructor() {
    super('This dancer\'s note for the night is already full. Shorten it and try again.');
    this.name = 'AttendanceNoteFullError';
  }
}

/** No signal, so nothing was saved. Never worded as saved. */
export class AttendanceNoteNeedsSignalError extends Error {
  readonly code = 'note-needs-signal' as const;
  constructor() {
    super('That note did not save, because notes need signal to save. Try again when you have signal.');
    this.name = 'AttendanceNoteNeedsSignalError';
  }
}

function deviceIsOffline(): boolean {
  const nav = (globalThis as { navigator?: { onLine?: boolean } }).navigator;
  return nav?.onLine === false;
}

function isOfflineError(err: unknown): boolean {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 'unavailable') return true;
  const message = err instanceof Error ? err.message : '';
  return /offline/i.test(message);
}

/**
 * Save one writer's line into the night's note, merged on the server's
 * current text inside a transaction.
 *
 * `merge` gets the note text as it is on the server right now and returns the
 * whole new text, or null when it would not fit. It may run more than once
 * (Firestore re-runs a transaction that lost a race), so it must be pure.
 */
export async function saveMergedAttendanceNote(
  firestore: Firestore,
  input: {
    uid: string;
    offeringId: string;
    date: string;
    studentId: string;
    /**
     * May return a promise only so the emulator race test can hold the first
     * attempt open while another phone writes; every app caller is a plain
     * synchronous merge.
     */
    merge: (current: string) => string | null | Promise<string | null>;
    timeoutMs?: number;
  }
): Promise<void> {
  if (deviceIsOffline()) throw new AttendanceNoteNeedsSignalError();
  const id = `${input.offeringId}_${input.date}_${input.studentId}`;
  const attendanceRef = doc(firestore, 'attendance', id);
  const noteRef = doc(firestore, 'attendanceNotes', id);
  // CANCELLED ONCE THE SCREEN HAS SAID "DID NOT SAVE" (#1293 re-check, N1,
  // proven in the emulator). Firestore cannot cancel a transaction, and one
  // still running after the timeout would RETRY with the draft it was given,
  // landing the teacher's OLD answer over the one she saved since. So the
  // timeout sets this flag, and every attempt checks it at the start and
  // again after the merge, BEFORE it stages a single write: an attempt that
  // finds it set throws, and a thrown error ends a transaction for good.
  let cancelled = false;
  const write = runTransaction(firestore, async (tx) => {
    if (cancelled) throw new AttendanceNoteNeedsSignalError();
    const snap = await tx.get(noteRef);
    const stored = snap.exists() ? snap.data()?.notes : '';
    const current = typeof stored === 'string' ? stored : '';
    const next = await input.merge(current);
    if (cancelled) throw new AttendanceNoteNeedsSignalError();
    if (next === null || next.length > ATTENDANCE_NOTE_TEXT_MAX) throw new AttendanceNoteFullError();
    tx.update(attendanceRef, { recordedBy: input.uid, updatedAt: serverTimestamp() });
    tx.set(
      noteRef,
      { offeringId: input.offeringId, date: input.date, notes: next, updatedAt: serverTimestamp() },
      { merge: false }
    );
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      cancelled = true;
      reject(new AttendanceNoteNeedsSignalError());
    }, input.timeoutMs ?? ATTENDANCE_NOTE_SAVE_TIMEOUT_MS);
  });
  try {
    await Promise.race([write, timeout]);
  } catch (err) {
    if (err instanceof AttendanceNoteFullError || err instanceof AttendanceNoteNeedsSignalError) throw err;
    if (isOfflineError(err)) throw new AttendanceNoteNeedsSignalError();
    throw err;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    // The one write that can still land after "did not save" is a commit
    // already in flight when the timer fired: it was merged on the server's
    // text a moment earlier, and anything the teacher saves next merges on top
    // of it. Every later attempt is stopped by `cancelled` above.
    write.catch(() => {});
  }
}
